import { type FC, useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { PALETTE_ROWS, isLightColor } from "../../../styles/colors";
import { RibbonCheckIcon as CheckIcon } from "../../atoms/Icon";

/** プリセット選択肢 */
export interface PresetOption {
    label: string;
    value: string;
}

export interface PaletteColorPickerProps {
    value: string;
    onChange: (hex: string) => void;
    /** パレット上部に表示するプリセット選択肢 */
    presetOptions?: PresetOption[];
    /** true の場合、ポップアップではなくインライン表示 */
    inline?: boolean;
}


/** プリセット + パレットグリッド本体 */
const PaletteGrid: FC<{
    value: string;
    onChange: (hex: string) => void;
    presetOptions: PresetOption[];
    onSelect?: () => void;
}> = ({ value, onChange, presetOptions, onSelect }) => (
    <div className="flex flex-col gap-1">
        {/* プリセット選択肢 */}
        {presetOptions.length > 0 && (
            <div className="flex flex-col gap-0.5 pb-1 mb-1 border-b border-gray-200">
                {presetOptions.map((preset) => {
                    const isActive = value === preset.value;
                    const isEmpty = preset.value === "";
                    return (
                        <button
                            key={preset.value}
                            type="button"
                            onClick={() => {
                                onChange(preset.value);
                                onSelect?.();
                            }}
                            className={clsx(
                                "flex items-center gap-1.5 px-1.5 py-1 rounded text-xs text-gray-600 hover:bg-gray-100 cursor-pointer",
                                isActive && "bg-gray-100 font-medium"
                            )}
                        >
                            {isEmpty ? (
                                <span className="inline-block h-4 w-4 rounded-sm border border-dashed border-gray-400 bg-[repeating-linear-gradient(-45deg,transparent,transparent_2px,#e5e7eb_2px,#e5e7eb_4px)]" />
                            ) : (
                                <span className="inline-block h-4 w-4 rounded-sm border border-gray-300" style={{ backgroundColor: preset.value }} />
                            )}
                            {preset.label}
                        </button>
                    );
                })}
            </div>
        )}
        {/* パレットグリッド */}
        {PALETTE_ROWS.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-1">
                {row.map((color) => {
                    const isPresetMatch = presetOptions.some((p) => p.value.toLowerCase() === color.hex.toLowerCase());
                    const isSelected = !isPresetMatch && value.toLowerCase() === color.hex.toLowerCase();
                    return (
                        <button
                            key={color.key}
                            type="button"
                            onClick={() => {
                                onChange(color.hex);
                                onSelect?.();
                            }}
                            className={clsx(
                                "h-6 w-6 rounded-sm cursor-pointer transition-transform hover:scale-110",
                                isSelected && "ring-2 ring-offset-1 ring-gray-800"
                            )}
                            style={{ backgroundColor: color.hex }}
                            title={color.label}
                        >
                            {isSelected && (
                                <CheckIcon
                                    className={clsx(
                                        "h-3.5 w-3.5 mx-auto",
                                        isLightColor(color.hex) ? "text-gray-800" : "text-white"
                                    )}
                                />
                            )}
                        </button>
                    );
                })}
            </div>
        ))}
    </div>
);

export const PaletteColorPicker: FC<PaletteColorPickerProps> = ({ value, onChange, presetOptions = [], inline = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    /** 現在の値がプリセットに該当するか */
    const matchedPreset = presetOptions.find((p) => p.value === value);
    /** 透明（色なし）プリセットが選択されているか */
    const isTransparent = value === "";

    // ボタン位置からポップオーバー座標を算出
    const updatePosition = useCallback(() => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        setPos({ top: rect.bottom + 4, left: rect.left });
    }, []);

    // 開閉切り替え
    const handleToggle = useCallback(() => {
        if (!isOpen) updatePosition();
        setIsOpen((prev) => !prev);
    }, [isOpen, updatePosition]);

    // 外部クリックで閉じる
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                buttonRef.current && !buttonRef.current.contains(target) &&
                popoverRef.current && !popoverRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    // スクロール時にも位置を追従
    useEffect(() => {
        if (!isOpen) return;
        const handleScroll = () => updatePosition();
        window.addEventListener("scroll", handleScroll, true);
        return () => window.removeEventListener("scroll", handleScroll, true);
    }, [isOpen, updatePosition]);

    // インラインモード: パレットをそのまま表示
    if (inline) {
        return (
            <PaletteGrid
                value={value}
                onChange={onChange}
                presetOptions={presetOptions}
            />
        );
    }

    // ポップアップモード（デフォルト）
    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={handleToggle}
                className={clsx(
                    "h-7 w-7 rounded border-2 cursor-pointer hover:border-brand-500 transition-colors",
                    isTransparent ? "border-dashed border-gray-400" : "border-gray-300"
                )}
                style={{ backgroundColor: isTransparent ? "transparent" : value }}
                title={matchedPreset ? matchedPreset.label : value}
            >
                {isTransparent && (
                    <span className="block h-full w-full bg-[repeating-linear-gradient(-45deg,transparent,transparent_2px,#e5e7eb_2px,#e5e7eb_4px)] rounded-sm" />
                )}
            </button>
            {isOpen && createPortal(
                <div
                    ref={popoverRef}
                    className="fixed z-[9999] p-2 bg-white rounded-lg shadow-lg border border-gray-200"
                    style={{ top: pos.top, left: pos.left }}
                >
                    <PaletteGrid
                        value={value}
                        onChange={onChange}
                        presetOptions={presetOptions}
                        onSelect={() => setIsOpen(false)}
                    />
                </div>,
                document.body
            )}
        </>
    );
};

export default PaletteColorPicker;
